import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend,
} from 'recharts';
import { TrendingUp, BarChart3, PieChart as PieIcon, Loader2 } from 'lucide-react';

const COL = { calories: '#E91E63', protein: '#EC4899', carbs: '#FBBF24', fat: '#34D399' };
const tipStyle = { background: '#18181b', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, color: '#fff', fontSize: 12 };

const fmtDay = (iso, en, compact) => {
  const d = new Date(iso + 'T00:00:00');
  return compact
    ? d.toLocaleDateString(en ? 'en-US' : 'fr-FR', { day: 'numeric', month: 'short' })
    : d.toLocaleDateString(en ? 'en-US' : 'fr-FR', { weekday: 'short', day: 'numeric' });
};

const CardBox = ({ icon: Icon, title, children, testid, headerExtra }) => (
  <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-4" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-primary" />
        <h4 className="font-bold text-white text-sm">{title}</h4>
      </div>
      {headerExtra}
    </div>
    {children}
  </div>
);

const PeriodToggle = ({ period, onChange, en }) => (
  <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)]" data-testid="chart-period-toggle">
    {[{ key: 'week', fr: '7 jours', enLbl: '7 days' }, { key: 'month', fr: '30 jours', enLbl: '30 days' }].map((p) => (
      <button key={p.key} onClick={() => onChange(p.key)}
        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
          period === p.key ? 'bg-gradient-to-r from-primary to-pink-500 text-white' : 'text-zinc-400 hover:text-white'
        }`}
        data-testid={`chart-period-${p.key}`}>
        {en ? p.enLbl : p.fr}
      </button>
    ))}
  </div>
);

export const NutritionCharts = ({ api, en }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const days = period === 'month' ? 30 : 7;

  useEffect(() => {
    setLoading(true);
    api(`/api/tracking/history?days=${days}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [api, days]);

  const periodLabel = en ? `last ${days} days` : `${days} derniers jours`;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!data || data.days_logged === 0) {
    return (
      <div className="space-y-3">
        <div className="flex justify-end"><PeriodToggle period={period} onChange={setPeriod} en={en} /></div>
        <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-10 text-center text-zinc-500 text-sm" data-testid="charts-empty">
          {en ? 'Log some meals to unlock your charts.' : 'Enregistrez des repas pour débloquer vos graphiques.'}
        </div>
      </div>
    );
  }

  const series = data.series.map((s) => ({ ...s, label: fmtDay(s.date, en, period === 'month') }));
  const ms = data.macro_split;
  const pie = [
    { name: en ? 'Protein' : 'Protéines', value: ms.protein_cal, g: ms.protein_g, color: COL.protein },
    { name: en ? 'Carbs' : 'Glucides', value: ms.carbs_cal, g: ms.carbs_g, color: COL.carbs },
    { name: en ? 'Fat' : 'Lipides', value: ms.fat_cal, g: ms.fat_g, color: COL.fat },
  ];
  const totalCal = pie.reduce((s, p) => s + p.value, 0) || 1;

  return (
    <div className="space-y-5" data-testid="nutrition-charts">
      <CardBox icon={TrendingUp} title={en ? `Calories — ${periodLabel}` : `Calories — ${periodLabel}`} testid="chart-calories"
        headerExtra={<PeriodToggle period={period} onChange={setPeriod} en={en} />}>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={series} margin={{ top: 8, right: 10, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={false} tickLine={false}
              interval={period === 'month' ? 3 : 0} />
            <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={false} tickLine={false} width={42} />
            <Tooltip contentStyle={tipStyle} />
            {data.goal_calories ? (
              <ReferenceLine y={data.goal_calories} stroke="#FBBF24" strokeDasharray="5 4"
                label={{ value: en ? 'Goal' : 'Objectif', fill: '#FBBF24', fontSize: 10, position: 'insideTopRight' }} />
            ) : null}
            <Line type="monotone" dataKey="calories" stroke={COL.calories} strokeWidth={3}
              dot={{ r: 3, fill: COL.calories }} activeDot={{ r: 5 }} name={en ? 'Calories' : 'Calories'} />
          </LineChart>
        </ResponsiveContainer>
      </CardBox>

      <CardBox icon={BarChart3} title={en ? `Macros per day (g) — ${periodLabel}` : `Macros par jour (g) — ${periodLabel}`} testid="chart-macros">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={series} margin={{ top: 8, right: 10, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={false} tickLine={false}
              interval={period === 'month' ? 3 : 0} />
            <YAxis tick={{ fill: '#a1a1aa', fontSize: 11 }} axisLine={false} tickLine={false} width={42} />
            <Tooltip contentStyle={tipStyle} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="protein" fill={COL.protein} name={en ? 'Protein' : 'Protéines'} radius={[3, 3, 0, 0]} />
            <Bar dataKey="carbs" fill={COL.carbs} name={en ? 'Carbs' : 'Glucides'} radius={[3, 3, 0, 0]} />
            <Bar dataKey="fat" fill={COL.fat} name={en ? 'Fat' : 'Lipides'} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardBox>

      <CardBox icon={PieIcon} title={en ? `Average macro split (${periodLabel})` : `Répartition moyenne des macros (${periodLabel})`} testid="chart-split">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="w-full sm:w-1/2 h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={3} stroke="none">
                  {pie.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={tipStyle} formatter={(v, n) => [`${Math.round((v / totalCal) * 100)}%`, n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2.5 w-full sm:w-1/2">
            {pie.map((p, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full" style={{ background: p.color }} />
                  <span className="text-sm text-zinc-300">{p.name}</span>
                </div>
                <span className="text-sm font-bold text-white">{Math.round((p.value / totalCal) * 100)}% · {p.g} g</span>
              </div>
            ))}
          </div>
        </div>
      </CardBox>
    </div>
  );
};

export default NutritionCharts;
